import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, MapPin, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { STAGES, telLink, waLink } from "@/lib/constants";
import { budgetRange, isOverdue, relDay } from "@/lib/format";
import { StageBadge, TempBadge, UserAvatar } from "@/components/common";
import { WaIcon } from "@/pages/DashboardPage";

const stop = (e) => e.stopPropagation();

export const ContactButtons = ({ lead, size = "sm" }) => (
  <div className="flex gap-1.5" onClick={stop}>
    <a href={telLink(lead.phone)} data-testid={`lead-call-btn-${lead.id}`} className={cn("flex items-center justify-center rounded-full border text-sky-600 transition-colors hover:bg-secondary", size === "lg" ? "h-10 w-10" : "h-8 w-8")} aria-label="Call">
      <Phone className="h-4 w-4" />
    </a>
    <a href={waLink(lead.phone)} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-btn-${lead.id}`} className={cn("flex items-center justify-center rounded-full border text-[#25D366] transition-colors hover:bg-secondary", size === "lg" ? "h-10 w-10" : "h-8 w-8")} aria-label="WhatsApp">
      <WaIcon />
    </a>
  </div>
);

export const FollowChip = ({ at }) => {
  if (!at) return null;
  const od = isOverdue(at);
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", od ? "font-semibold text-destructive" : "text-muted-foreground")} data-testid={od ? "followup-overdue" : "followup-date"}>
      <CalendarClock className="h-3.5 w-3.5" />
      {od ? "Overdue · " : ""}
      {relDay(at)}
    </span>
  );
};

export function LeadCard({ lead, owner, onMove, compact }) {
  const nav = useNavigate();
  const od = isOverdue(lead.next_followup_at) && !lead.stage.startsWith("Closed");
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`lead-card-${lead.id}`}
      onClick={() => nav(`/leads/${lead.id}`)}
      className={cn(
        "group cursor-pointer rounded-xl border bg-card p-4 transition-colors hover:border-primary/40",
        od && "border-l-4 border-l-destructive",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold">{lead.name}</p>
          <p className="num text-xs text-muted-foreground">{lead.phone}</p>
        </div>
        <TempBadge t={lead.temperature} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        {!compact && <StageBadge stage={lead.stage} />}
        <span>{lead.lead_type}{lead.property_type ? ` · ${lead.property_type}` : ""}{lead.bhk ? ` · ${lead.bhk}` : ""}</span>
      </div>
      <p className="num mt-1.5 text-sm font-medium">{budgetRange(lead.budget_min, lead.budget_max)}</p>
      {lead.locations?.length > 0 && (
        <p className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground"><MapPin className="h-3 w-3 shrink-0" />{lead.locations.join(", ")}</p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {owner && <UserAvatar user={owner} />}
          <FollowChip at={lead.stage.startsWith("Closed") ? null : lead.next_followup_at} />
        </div>
        <ContactButtons lead={lead} />
      </div>
      {onMove && (
        <select
          onClick={stop}
          value={lead.stage}
          onChange={(e) => onMove(lead, e.target.value)}
          data-testid={`lead-move-select-${lead.id}`}
          className="mt-3 h-8 w-full rounded-md border bg-background px-2 text-xs md:hidden"
        >
          {STAGES.map((s) => <option key={s} value={s}>Move to: {s}</option>)}
        </select>
      )}
    </div>
  );
}

export const LeadLink = ({ id, children }) => <Link to={`/leads/${id}`}>{children}</Link>;
